import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  User,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { auth, firebaseConfigStatus } from '../lib/firebase';
import { setApiTokenProvider } from '../lib/api/client';
import { measureProductAction, resetProductAnalytics } from '../lib/productAnalytics';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOutAdmin: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function friendlyAuthError(error: unknown): string {
  if (error instanceof FirebaseError) {
    switch (error.code) {
      case 'auth/invalid-credential':
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        return 'The email or password is incorrect. Check both fields and try again.';
      case 'auth/invalid-email':
        return 'Enter a valid email address.';
      case 'auth/too-many-requests':
        return 'Too many sign-in attempts. Wait a few minutes, then try again.';
      case 'auth/network-request-failed':
        return 'The sign-in service could not be reached. Check your connection and try again.';
      case 'auth/user-disabled':
        return 'This admin account has been disabled.';
      default:
        return 'We could not sign you in. Please try again.';
    }
  }
  return error instanceof Error ? error.message : 'We could not sign you in. Please try again.';
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const previousUserId = React.useRef<string | null | undefined>(undefined);

  const getIdToken = useCallback(async () => {
    if (!auth) {
      return null;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      return null;
    }

    return currentUser.getIdToken();
  }, []);

  React.useEffect(() => {
    setApiTokenProvider(getIdToken);

    if (!auth) {
      setLoading(false);
      setError(
        `Firebase auth is not configured. Missing: ${firebaseConfigStatus.missingEnvKeys.join(', ')}`
      );
      return () => {
        setApiTokenProvider(null);
      };
    }

    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      const nextId = nextUser?.uid ?? null;
      if (previousUserId.current != null && previousUserId.current !== nextId) {
        resetProductAnalytics();
      }
      previousUserId.current = nextId;
      setUser(nextUser);
      setLoading(false);
    });

    return () => {
      setApiTokenProvider(null);
      unsubscribe();
    };
  }, [getIdToken]);

  const signIn = useCallback(async (email: string, password: string) => {
    setError(null);
    if (!auth) {
      const configError = new Error(
        `Firebase auth is not configured. Missing: ${firebaseConfigStatus.missingEnvKeys.join(', ')}`
      );
      setError(configError.message);
      throw configError;
    }

    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      const message = friendlyAuthError(err);
      setError(message);
      throw new Error(message);
    }
  }, []);

  const signOutAdmin = useCallback(async () => {
    setError(null);
    if (!auth) {
      return;
    }
    const currentAuth = auth;
    await measureProductAction('sign_out', () => signOut(currentAuth));
    resetProductAnalytics();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      error,
      signIn,
      signOutAdmin,
      getIdToken,
    }),
    [error, getIdToken, loading, signIn, signOutAdmin, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
